import Alpine from 'alpinejs'
import * as animate from '@js/util/animate.js'

function callMeMaybe(value) {
  return typeof value === 'function' ? value() : value
}

/**
 * Alpine.js x-data module for toggling element visibility with animation.
 *
 * Usage:
 * <div x-data="transition({ css: { opacity: 0 } })">
 *   <button @click="toggle()">Toggle</button>
 *   <div x-bind="target">I can fade!</div>
 * </div>
 *
 * Can also be used with object spreading to combine with other data:
 * <div x-data="{
 *   otherData: 'something',
 *   ...transition({ css: { opacity: 0 } })
 * }">
 *   ...
 * </div>
 *
 * Options:
 * css             CSS state to animate to/from
 *                 Shorthand for defining enter.css and leave.css options
 *                 Can be a function to be lazily evaluated
 * enter           Options for imported 'animate' function on entering,
 *                 plus a 'css' property to define the CSS state to animate to
 * leave           Options for imported 'animate' function on leaving,
 *                 plus a 'css' property to define the CSS state to animate to
 * bind            Bind visibility to some external state instead of managing it locally.
 *                 This can be:
 *                 - a string (which establishes a two-way binding with an Alpine store)
 *
 *                   Example:
 *                   { bind: 'myValue' }
 *
 *                   Binds visibility to Alpine.store('myValue')
 *
 *                 - a getter which returns the (reactive) state
 *                   (the getter will be a read-only source, show()/hide()/toggle() will be no-ops)
 *
 *                   Example:
 *                   { get bind() { return $breakpoints('md') } }
 *
 *                   Binds visibility to the 'md' breakpoint
 *                   (given that a magic $breakpoints variable exists)
 *
 *                 - a getter and setter (getter will be watched for changes, local state changes
 *                   will be reflected back to the setter)
 *
 *                   Example:
 *                   {
 *                     get bind() { return $hash },
 *                     set bind(value) { location.hash = value }
 *                   }
 *
 *                   Binds visibility to the location hash (given that a magic $hash variable exists
 *                   that can read and manipulate the hash)
 * initialState    Whether the element should be visible initially (ignored if 'bind' option is used)
 * ...options      Other options from the imported 'animate' function are supported
 *
 * Exposed data:
 * target                  Use x-bind="target" on element(s) to animate
 * isVisible()             Whether target elements *should be* visible
 * isEffectivelyVisible()  Whether target elements *are* effectively visible
 *                         (changes before element enters/after element leaves)
 * isAnimating()           Checks whether the passed element is currently being animated
 * show()                  Method to show target elements
 * hide()                  Method to hide target elements, supports a 'delay' option
 *                         hide({ delay: 500 }) waits 500ms before hiding, if element is being re-shown
 *                         in that time, hiding is canceled
 * toggle()                Toggles visibility, accepts a boolean option to force certain state
 */
Alpine.data('transition', options => {
  let bind = Object.getOwnPropertyDescriptor(options, 'bind')

  let { css, enter, leave, initialState = false, ...animateOptions } = options
  delete animateOptions.bind

  if ((enter || leave) && css) {
    throw new Error(
      'Cannot use "css" option together with either "enter" or "leave". Either specify "css" or "enter" + "leave".',
    )
  }

  if ((enter && !leave) || (!enter && leave)) {
    throw new Error(
      'Specifying "enter" transitions requires specifying "leave" transitions as well',
    )
  }

  let enterCss, leaveCss, enterOptions, leaveOptions
  if (!enter) {
    enterCss = leaveCss = css
    enterOptions = leaveOptions = animateOptions
  } else {
    ;({ css: enterCss, ...enterOptions } = enter)
    ;({ css: leaveCss, ...leaveOptions } = leave)
  }

  let state = Alpine.reactive({
    // The current visibility state, only used if no 'bind' is set
    state: initialState,

    // Effective visibility, before animation starts/after animation ends
    visible: initialState,

    // Whether an opposing animation is queued up already
    queued: new WeakSet(),

    // Set of elements being currently animated
    runningAnimations: new WeakSet(),

    // Whether the target element is about to hide after a delay
    willHide: false,

    // Components which have been initialized
    initialized: new WeakSet(),
  })

  let hideTimeout = 0

  // Internal 'show' method
  function show(element) {
    element.style.removeProperty('display')

    state.runningAnimations.add(element)
    state.effectivelyVisible = true

    animate.from(element, callMeMaybe(enterCss), enterOptions).then(() => {
      state.runningAnimations.delete(element)

      if (state.queued.has(element)) {
        state.queued.delete(element)
        hide(element)
      }
    })
  }

  // Internal 'hide' method
  function hide(element) {
    state.runningAnimations.add(element)
    animate.to(element, callMeMaybe(leaveCss), leaveOptions).then(() => {
      element.style.display = 'none'
      state.effectivelyVisible = false
      state.runningAnimations.delete(element)

      if (state.queued.has(element)) {
        state.queued.delete(element)
        show(element)
      }
    })
  }

  let readExternal, writeExternal
  if (bind) {
    if (typeof bind.get === 'function') {
      readExternal = () => {
        return bind.get()
      }
    } else if (typeof bind.value === 'string') {
      readExternal = () => Alpine.store(bind.value)
    }

    if (typeof bind.set === 'function') {
      writeExternal = newState => bind.set(newState)
    } else if (typeof bind.value === 'string') {
      writeExternal = newState => Alpine.store(bind.value, newState)
    }
  }

  // Public API
  return {
    isVisible() {
      return readExternal ? readExternal() : state.visible
    },

    isEffectivelyVisible() {
      return state.effectivelyVisible
    },

    isAnimating(element) {
      return state.runningAnimations.has(element)
    },

    // x-bind property for the shown/hidden target
    // Can be used on multiple elements
    target: {
      'x-init'() {
        if (state.initialized.has(this.$el)) return

        let element = this.$el

        state.initialized.add(element)
        if (readExternal) {
          let initialState = readExternal()
          state.effectivelyVisible = initialState

          if (!initialState) {
            element.style.display = 'none'
          } else {
            element.style.removeProperty('display')
          }
        } else {
          state.visible = element.style.display !== 'none'
          state.effectivelyVisible = state.visible
        }

        let watchedState = readExternal || (() => state.visible)

        let previousState = Boolean(watchedState())
        this.$watch(watchedState, newState => {
          newState = Boolean(newState)
          if (newState === previousState) return
          previousState = newState

          if (state.runningAnimations.has(element)) {
            // Property alternates between boolean states because
            // clicking 2x/4x/6x etc. during a running animation results in the
            // same end state and thus removes the need for queuing up anything
            if (state.queued.has(element)) {
              state.queued.delete(element)
            } else {
              state.queued.add(element)
            }
          } else {
            if (newState) {
              show(element)
            } else {
              hide(element)
            }
          }
        })
      },
    },

    // Public 'show' method
    show() {
      clearTimeout(hideTimeout)

      this.toggle(true)
    },

    // Public 'hide' method
    hide({ delay = 0 } = {}) {
      if (!delay) {
        this.toggle(false)
      } else {
        state.willHide = true
        hideTimeout = setTimeout(() => {
          this.toggle(false)
        }, delay)
      }
    },

    // Public 'toggle' method
    toggle(newState) {
      state.willHide = false

      if (writeExternal) {
        if (typeof newState !== 'boolean') {
          newState = !readExternal()
        }

        writeExternal(newState)
      } else {
        if (typeof newState !== 'boolean') {
          newState = !state.visible
        }

        state.visible = newState
      }
    },
  }
})
